import Udesk from 'Udesk';
import React, { FC, memo, useMemo, useState, useCallback } from 'react';
import { Col, Row, Input, Spin, Empty } from 'udesk-ui';
import { Content, Wrapper } from './components';
import { putDataByIdCustomFields } from 'src/api/data/{id}/custom-fields';

interface CustomField {
    name: string;
    value: string;
}

interface TemplateProps {
    style?: React.CSSProperties;
    height?: number;
    callId?: number;
    isEditable?: boolean;
    editableLoading?: boolean;
    customFields?: CustomField[];
}

const Template: FC<TemplateProps> = memo((props) => {
    const { style, height, callId, isEditable, editableLoading, customFields } = props;

    const contentHeight = useMemo(() => {
        return height && height >= 38 ? height - 38 : undefined;
    }, [height]);

    // 本地状态管理字段值
    const [localFields, setLocalFields] = useState<CustomField[]>(customFields || []);
    // 已成功保存的字段值，用于失焦时判断是否真正变化，以及保存失败时回滚
    const [savedFields, setSavedFields] = useState<CustomField[]>(customFields || []);

    // 同步外部数据变化
    React.useEffect(() => {
        setLocalFields(customFields || []);
        setSavedFields(customFields || []);
    }, [customFields]);

    const handleBlur = useCallback(
        (index: number, name: string, newValue: string) => {
            const oldValue = savedFields[index]?.value || '';
            if (newValue !== oldValue) {
                putDataByIdCustomFields({
                    segments: { id: callId! },
                    data: { customFieldList: [{ id: name, value: newValue }] },
                }).then(
                    () => {
                        setSavedFields((prevFields) => {
                            const newFields = [...prevFields];
                            newFields[index] = { ...newFields[index], value: newValue };
                            return newFields;
                        });
                        Udesk.ui.notify.success('保存成功');
                    },
                    () => {
                        setLocalFields((prevFields) => {
                            const newFields = [...prevFields];
                            newFields[index] = { ...newFields[index], value: oldValue };
                            return newFields;
                        });
                        Udesk.ui.notify.error('保存失败');
                    }
                );
            }
        },
        [callId, savedFields]
    );

    const handleChange = useCallback(
        (index: number, value: string) => {
            const newFields = [...localFields];
            newFields[index] = { ...newFields[index], value };
            setLocalFields(newFields);
        },
        [localFields]
    );

    return (
        <Wrapper style={{ height, ...style }}>
            <Spin spinning={editableLoading}>
                {localFields?.length ? (
                    <Content height={contentHeight}>
                        {localFields.map((field, idx) => (
                            <Row key={idx} style={{ marginBottom: 6 }} gutter={4} wrap={false}>
                                <Col flex="120px">
                                    <span
                                        style={{
                                            width: '100%',
                                            textAlign: 'right',
                                            display: 'block',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}
                                        title={field.name}
                                    >
                                        {field.name}：
                                    </span>
                                </Col>
                                <Col flex="auto">
                                    {isEditable ? (
                                        <Input
                                            value={field.value}
                                            onChange={(e) => handleChange(idx, e.target.value)}
                                            onBlur={(e) =>
                                                handleBlur(idx, field.name, e.target.value)
                                            }
                                            size="small"
                                            style={{ width: '100%' }}
                                        />
                                    ) : (
                                        <span
                                            style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                display: 'block',
                                            }}
                                            title={field.value}
                                        >
                                            {field.value}
                                        </span>
                                    )}
                                </Col>
                            </Row>
                        ))}
                    </Content>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Spin>
        </Wrapper>
    );
});

export default Template;
